import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { ErrorMsg, Loading, Modal } from '../../components/ui';
import { api, ApiError, errorText } from '../../lib/api';
import type { Asset, Brand, ContentFormat, ImageLayer, ItemDetail, Layer, Layout, Revision, TemplateDetail, Warning } from '../../lib/contentTypes';
import { useSession } from '../../lib/session';
import { ContentStatusBadge, FORMAT_LABEL } from './common';
import { ArtboardPanel, CopyPanel, Inspector, RevisionsPanel, WorkflowPanel } from './EditorPanels';
import { ensureFonts } from './fonts';
import { PostPreview } from './Preview';
import { loadImage, logoFor, renderArtboard, renderToCanvas, type RenderEnv } from './render';
import { ghostsFor, HANDLES, panFocal, resizeRect, SNAP_PX, snapMove, snapTargets, type Guide, type Handle, type Targets } from './snap';

const LOCKED_STATUSES = ['approved', 'exported', 'published'];
let uid = 0;
export const newId = (p: string) => `${p}${Date.now().toString(36)}${(uid++).toString(36)}`;
const clone = <T,>(x: T): T => structuredClone(x);

declare global {
  interface Window {
    /** QA hook for the smoke test: full-size render of one artboard of the layout on screen. */
    __mmContentQA?: { render: (index: number, opts?: { fontFallback?: boolean }) => string; revision: () => number };
  }
}

/** What the editor edits and saves to: a content item (revisions, approval) or a template (draft). */
export type EditorSource = { kind: 'item'; id: string } | { kind: 'template'; id: string };

export interface EditorApi {
  layout: Layout;
  /** the brand kit, the format and the project of whatever is being edited (null project for a template) */
  brand: Brand;
  format: ContentFormat;
  projectId: string | null;
  mode: 'item' | 'template';
  sel: { ab: number; layer: string | null };
  setSel: (s: { ab: number; layer: string | null }) => void;
  commit: (fn: (draft: Layout) => void, coalesceKey?: string) => void;
  readOnly: boolean;
  assets: Map<string, Asset>;
  allAssets: Asset[];
  warnings: Warning[];
  imageSize: (assetId: string | null) => { w: number; h: number } | null;
}

/** The page of a content item. */
export function Editor() {
  const { id } = useParams();
  return <EditorCore key={id} source={{ kind: 'item', id: id! }} />;
}

/** The editor itself: canvas, overlay, panels, history and autosave. Where it loads from and saves to
 *  is the `source` (a content item, or a template's draft); the item page keeps its revisions,
 *  approval and export panels, a template gets publish / discard instead. */
export function EditorCore({ source }: { source: EditorSource }) {
  const id = source.id;
  const isTemplate = source.kind === 'template';
  const navigate = useNavigate();
  const { user } = useSession();
  const [item, setItem] = useState<ItemDetail | null>(null);
  const [template, setTemplate] = useState<TemplateDetail | null>(null);
  const [brand, setBrand] = useState<Brand | null>(null);
  const [layout, setLayout] = useState<Layout | null>(null);
  const [base, setBase] = useState(0);
  const [undo, setUndo] = useState<Layout[]>([]);
  const [redo, setRedo] = useState<Layout[]>([]);
  const [sel, setSel] = useState<{ ab: number; layer: string | null }>({ ab: 0, layer: null });
  const [allAssets, setAllAssets] = useState<Asset[]>([]);
  const [images, setImages] = useState<Map<string, HTMLImageElement>>(new Map());
  const [logos, setLogos] = useState<Map<string, HTMLImageElement>>(new Map());
  const [fontsReady, setFontsReady] = useState(false);
  const [unlocked, setUnlocked] = useState(false);
  const [viewing, setViewing] = useState<Revision | null>(null);
  const [save, setSave] = useState<{ state: 'idle' | 'pending' | 'saving' | 'saved' | 'error'; msg?: string; conflict?: boolean }>({ state: 'idle' });
  const [warnings, setWarnings] = useState<Warning[]>([]);
  const [loadError, setLoadError] = useState('');
  const [templateDialog, setTemplateDialog] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [tplBusy, setTplBusy] = useState('');
  const [tplError, setTplError] = useState('');

  const load = useCallback(async () => {
    try {
      if (isTemplate) {
        const [t, b] = await Promise.all([api<TemplateDetail>(`/api/content/templates/${id}`), api<Brand>('/api/content/brand')]);
        setTemplate(t);
        setBrand(b);
        setLayout(t.editor_layout);
        setBase(t.draft_revision);
      } else {
        const d = await api<ItemDetail>(`/api/content-items/${id}`);
        setItem(d);
        setBrand(d.brand);
        if (d.revision) {
          setLayout(d.revision.layout);
          setBase(d.revision.number);
        }
      }
      setUndo([]);
      setRedo([]);
      setUnlocked(false);
      setViewing(null);
      setSave({ state: 'idle' });
    } catch (e) {
      setLoadError(errorText(e));
    }
  }, [id, isTemplate]);
  useEffect(() => {
    void load();
    api<{ items: Asset[] }>('/api/content/assets').then((d) => setAllAssets(d.items));
    ensureFonts().then(() => setFontsReady(true), (e) => setLoadError(`fonts could not be loaded: ${errorText(e)}`));
  }, [load]);

  const assets = useMemo(() => {
    const m = new Map<string, Asset>();
    for (const a of allAssets) m.set(a.id, a);
    for (const a of item?.assets ?? []) m.set(a.id, a);
    return m;
  }, [allAssets, item]);

  const shown = viewing?.layout ?? layout; // an older revision is shown read-only
  // load every image the shown layout uses (the work file: what the warnings measure)
  const wanted = useMemo(
    () => Array.from(new Set((shown?.artboards ?? []).flatMap((a) => a.layers.flatMap((l) => (l.type === 'image' && l.asset_id ? [l.asset_id] : []))))).sort().join(','),
    [shown],
  );
  useEffect(() => {
    const ids = wanted ? wanted.split(',') : [];
    const missing = ids.filter((x) => !images.has(x) && assets.has(x));
    if (!missing.length) return;
    let alive = true;
    Promise.all(missing.map((x) => loadImage(assets.get(x)!.urls.work).then((img) => [x, img] as const).catch(() => null))).then((rows) => {
      if (!alive) return;
      setImages((prev) => {
        const next = new Map(prev);
        rows.forEach((r) => r && next.set(r[0], r[1]));
        return next;
      });
    });
    return () => {
      alive = false;
    };
  }, [wanted, assets, images]);
  useEffect(() => {
    // every brand logo version, so a logo layer can draw the one it chose
    const list = brand?.logos ?? [];
    let alive = true;
    Promise.all(list.map((v) => loadImage(v.asset.urls.work).then((img) => [v.asset_id, img] as const, () => null))).then((rows) => {
      if (alive) setLogos(new Map(rows.filter((r): r is readonly [string, HTMLImageElement] => r !== null)));
    });
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [(brand?.logos ?? []).map((v) => v.asset.urls.work).join('|')]);

  const status = item?.status;
  const readOnly = isTemplate ? !!template?.is_builtin : !!viewing || status === 'archived' || (LOCKED_STATUSES.includes(status ?? '') && !unlocked);

  // --- history and autosave ------------------------------------------------------------------
  const layoutRef = useRef(layout);
  layoutRef.current = layout;
  const baseRef = useRef(base);
  baseRef.current = base;
  const warningsRef = useRef(warnings);
  warningsRef.current = warnings;
  const lastCoalesce = useRef<{ key: string; at: number } | null>(null);
  const saveTimer = useRef<number | undefined>(undefined);

  const doSave = useCallback(async () => {
    const current = layoutRef.current;
    if (!current) return;
    setSave({ state: 'saving' });
    try {
      let msg: string | undefined;
      if (isTemplate) {
        // autosave writes the template's DRAFT; the published version waits for "Publish"
        const t = await api<TemplateDetail>(`/api/content/templates/${id}`, { method: 'PUT', body: { base_revision: baseRef.current, layout: current } });
        setTemplate(t);
        setBase(t.draft_revision);
        baseRef.current = t.draft_revision;
      } else {
        const r = await api<{ revision: Revision; created: boolean; approval_revoked: boolean; status: string }>(`/api/content-items/${id}/layout`, {
          method: 'PUT',
          body: { base_revision: baseRef.current, layout: current, warnings: warningsRef.current },
        });
        setBase(r.revision.number);
        baseRef.current = r.revision.number;
        msg = r.approval_revoked ? 'Saved; the approval was revoked by this edit.' : undefined;
        if (r.created) {
          const d = await api<ItemDetail>(`/api/content-items/${id}`);
          setItem(d);
        }
      }
      setSave({ state: layoutRef.current === current ? 'saved' : 'pending', msg });
      if (layoutRef.current !== current) saveTimer.current = window.setTimeout(() => void doSave(), 400);
    } catch (e) {
      setSave({ state: 'error', msg: errorText(e), conflict: e instanceof ApiError && e.status === 409 });
    }
  }, [id, isTemplate]);

  const commit = useCallback(
    (fn: (draft: Layout) => void, coalesceKey?: string) => {
      const prev = layoutRef.current;
      if (!prev) return;
      const next = clone(prev);
      fn(next);
      const now = Date.now();
      // a drag is one undo step however long the pointer rests; typing coalesces within 1.2 s
      const same = coalesceKey && lastCoalesce.current?.key === coalesceKey && (coalesceKey.startsWith('drag:') || now - lastCoalesce.current.at < 1200);
      if (!same) setUndo((u) => [...u.slice(-99), prev]);
      lastCoalesce.current = coalesceKey ? { key: coalesceKey, at: now } : null;
      setRedo([]);
      layoutRef.current = next;
      setLayout(next);
      setSave({ state: 'pending' });
      window.clearTimeout(saveTimer.current);
      saveTimer.current = window.setTimeout(() => void doSave(), 1200);
    },
    [doSave],
  );
  const step = useCallback(
    (dir: 'undo' | 'redo') => {
      const from = dir === 'undo' ? undo : redo;
      if (!from.length || !layoutRef.current) return;
      const target = from[from.length - 1];
      const cur = layoutRef.current;
      if (dir === 'undo') {
        setUndo(undo.slice(0, -1));
        setRedo([...redo, cur]);
      } else {
        setRedo(redo.slice(0, -1));
        setUndo([...undo, cur]);
      }
      lastCoalesce.current = null;
      layoutRef.current = target;
      setLayout(target);
      setSave({ state: 'pending' });
      window.clearTimeout(saveTimer.current);
      saveTimer.current = window.setTimeout(() => void doSave(), 1200);
    },
    [undo, redo, doSave],
  );
  useEffect(() => () => window.clearTimeout(saveTimer.current), []);

  // --- keyboard: registered once, reads state through refs (M-090) ---------------------------
  const keyState = useRef({ step, commit, sel, readOnly, layout });
  keyState.current = { step, commit, sel, readOnly, layout };
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement | null;
      if (t && (t.closest('input, textarea, select, [contenteditable]') || document.querySelector('.modal, .fb-dialog'))) return;
      const k = keyState.current;
      if (k.readOnly) return;
      const mod = e.ctrlKey || e.metaKey;
      if (mod && e.key.toLowerCase() === 'z') {
        e.preventDefault();
        k.step(e.shiftKey ? 'redo' : 'undo');
        return;
      }
      if (mod && e.key.toLowerCase() === 'y') {
        e.preventDefault();
        k.step('redo');
        return;
      }
      const layerId = k.sel.layer;
      if (!layerId) return;
      const d = e.shiftKey ? 10 : 1;
      const move: Record<string, [number, number]> = { ArrowLeft: [-d, 0], ArrowRight: [d, 0], ArrowUp: [0, -d], ArrowDown: [0, d] };
      if (move[e.key]) {
        e.preventDefault();
        k.commit((l) => {
          const layer = l.artboards[k.sel.ab].layers.find((x) => x.id === layerId);
          if (layer) {
            layer.x += move[e.key][0];
            layer.y += move[e.key][1];
          }
        }, `nudge:${layerId}`);
      } else if (e.key === 'Delete' || e.key === 'Backspace') {
        e.preventDefault();
        k.commit((l) => {
          l.artboards[k.sel.ab].layers = l.artboards[k.sel.ab].layers.filter((x) => x.id !== layerId);
        });
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // --- rendering -------------------------------------------------------------------------------
  const env: RenderEnv | null = useMemo(() => {
    if (!brand) return null;
    const usage = new Map<string, string>();
    assets.forEach((a, k) => usage.set(k, a.usage_status));
    const logo = brand.logo_asset_id ? (logos.get(brand.logo_asset_id) ?? null) : null;
    // a template draws its empty image boxes as "Image N" placeholders instead of warning about them
    return { brand: brand.spec, images, usage, logo, logos, placeholders: isTemplate };
  }, [brand, images, assets, logos, isTemplate]);

  const stageRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [stageW, setStageW] = useState(600);
  useEffect(() => {
    const el = stageRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => setStageW(el.clientWidth));
    ro.observe(el);
    setStageW(el.clientWidth);
    return () => ro.disconnect();
  }, [brand]);
  const scale = shown ? Math.min(stageW / shown.width, (window.innerHeight * 0.72) / shown.height) : 1;
  const ab = Math.min(sel.ab, (shown?.artboards.length ?? 1) - 1);

  useEffect(() => {
    const c = canvasRef.current;
    if (!c || !shown || !env || !fontsReady) return;
    const dpr = window.devicePixelRatio || 1;
    c.width = Math.round(shown.width * scale * dpr);
    c.height = Math.round(shown.height * scale * dpr);
    const ctx = c.getContext('2d')!;
    ctx.setTransform(scale * dpr, 0, 0, scale * dpr, 0, 0);
    renderArtboard(ctx, shown, shown.artboards[ab], ab, env);
    // warnings for every artboard (measured at full scale; drawing is the same code path)
    const all: Warning[] = [];
    shown.artboards.forEach((_, i) => {
      const off = document.createElement('canvas');
      off.width = 1;
      off.height = 1;
      all.push(...renderArtboard(off.getContext('2d')!, shown, shown.artboards[i], i, env));
    });
    setWarnings(all);
  }, [shown, env, fontsReady, scale, ab]);

  useEffect(() => {
    if (!shown || !env) return;
    window.__mmContentQA = {
      render: (index, opts) => renderToCanvas(shown, index, { ...env, fontFallback: opts?.fontFallback }).canvas.toDataURL('image/png'),
      revision: () => baseRef.current,
    };
    return () => {
      delete window.__mmContentQA;
    };
  }, [shown, env]);

  const imageSize = useCallback(
    (assetId: string | null) => {
      const img = assetId ? images.get(assetId) : undefined;
      return img ? { w: img.naturalWidth, h: img.naturalHeight } : null;
    },
    [images],
  );

  if (loadError) return <ErrorMsg>{loadError}</ErrorMsg>;
  if (!brand || !layout || !shown || (!item && !template)) return <Loading what="Loading the editor" />;

  const editorApi: EditorApi = {
    layout: shown,
    brand,
    format: (item?.format ?? template?.format)!,
    projectId: item?.project_id ?? null,
    mode: source.kind,
    sel: { ...sel, ab },
    setSel,
    commit,
    readOnly,
    assets,
    allAssets,
    warnings,
    imageSize,
  };
  const dirty = save.state === 'pending' || save.state === 'saving';
  const saveError = save.state === 'error' && (
    <div className="msg err" role="alert">
      Not saved: {save.msg}{' '}
      {save.conflict && (
        <button type="button" className="btn small" onClick={() => void load()}>
          {isTemplate ? 'Reload the template' : 'Reload the latest revision'}
        </button>
      )}
    </div>
  );
  const saveBadge = (
    <span className="cs-save" data-testid="save-state" data-state={save.state}>
      {save.state === 'saving' ? 'saving…' : save.state === 'pending' ? 'unsaved changes' : save.state === 'saved' ? 'saved' : ''}
    </span>
  );
  const undoRedo = (
    <>
      <button type="button" className="btn small" onClick={() => step('undo')} disabled={readOnly || !undo.length}>
        Undo
      </button>
      <button type="button" className="btn small" onClick={() => step('redo')} disabled={readOnly || !redo.length}>
        Redo
      </button>
      <button type="button" className="btn small" onClick={() => setPreviewOpen(true)} disabled={!env || !fontsReady}>
        Preview
      </button>
    </>
  );
  const center = (
    <div className="cs-center">
      <div ref={stageRef} className="cs-stage-wrap">
        {!fontsReady ? (
          <Loading what="Loading fonts" />
        ) : (
          <div className="cs-stage" style={{ width: shown.width * scale, height: shown.height * scale }}>
            <canvas ref={canvasRef} className="cs-canvas" style={{ width: shown.width * scale, height: shown.height * scale }} aria-label={`Preview of artboard ${ab + 1}`} role="img" />
            <Overlay api={editorApi} scale={scale} env={env} />
          </div>
        )}
      </div>
      <WarningsList warnings={warnings} onPick={(w) => setSel({ ab: w.artboard, layer: w.layer ?? null })} template={isTemplate} />
    </div>
  );

  if (template) {
    const act = async (what: 'publish' | 'discard' | 'duplicate') => {
      setTplBusy(what);
      setTplError('');
      try {
        const t = await api<TemplateDetail>(`/api/content/templates/${template.id}/${what}`, { method: 'POST' });
        if (what === 'duplicate') {
          navigate(`/content/templates/${t.id}`);
          return;
        }
        setTemplate(t);
        if (what === 'discard') {
          // back to the published version
          layoutRef.current = t.editor_layout;
          setLayout(t.editor_layout);
          setUndo([]);
          setRedo([]);
        }
        setBase(t.draft_revision);
        baseRef.current = t.draft_revision;
        setSave({ state: 'idle' });
      } catch (e) {
        setTplError(errorText(e));
      } finally {
        setTplBusy('');
      }
    };
    const rename = async (name: string) => {
      if (!name.trim() || name === template.name) return;
      try {
        const t = await api<TemplateDetail>(`/api/content/templates/${template.id}`, { method: 'PUT', body: { base_revision: baseRef.current, name } });
        setTemplate(t);
      } catch (e) {
        setTplError(errorText(e));
      }
    };
    return (
      <div className="page cs-editor-page" data-template-editor={template.id}>
        <p className="crumbs small">
          <Link to="/content">Content Studio</Link> · <Link to="/content/templates">Templates</Link>
        </p>
        <header className="cs-editor-head">
          <div>
            {template.is_builtin ? (
              <h1 className="cs-title">{template.name}</h1>
            ) : (
              <h1 className="cs-title">
                <input className="cs-title-input" defaultValue={template.name} key={template.name} aria-label="Template name" onBlur={(ev) => void rename(ev.target.value)} />
              </h1>
            )}
            <p className="muted small">
              {FORMAT_LABEL[template.format]} · <span className="num">{template.width}×{template.height}</span> px · <TemplateStatus t={template} />{' '}
              {template.unpublished_changes && (
                <span className="badge tone-warn" data-testid="unpublished">
                  unpublished changes
                </span>
              )}{' '}
              {saveBadge}
            </p>
          </div>
          <div className="row gap wrap">
            {undoRedo}
            {template.is_builtin ? (
              <button type="button" className="btn primary small" disabled={!!tplBusy} onClick={() => void act('duplicate')}>
                Duplicate to edit
              </button>
            ) : (
              <>
                <button type="button" className="btn small" disabled={!!tplBusy} onClick={() => void act('duplicate')}>
                  Duplicate
                </button>
                {template.unpublished_changes && (
                  <button type="button" className="btn small" disabled={!!tplBusy || dirty} onClick={() => void act('discard')}>
                    Discard changes
                  </button>
                )}
                <button
                  type="button"
                  className="btn primary small"
                  disabled={!!tplBusy || dirty || (template.status === 'ready' && !template.unpublished_changes)}
                  onClick={() => void act('publish')}
                >
                  Publish
                </button>
              </>
            )}
          </div>
        </header>
        {saveError}
        <ErrorMsg>{tplError}</ErrorMsg>
        {template.is_builtin ? (
          <p className="msg info">Built-in templates are shown as shipped and cannot be changed. Duplicate it to make your own Draft.</p>
        ) : template.status === 'draft' ? (
          <p className="msg info">This template is a Draft: your changes save by themselves, but “New content item” does not offer it until you publish it.</p>
        ) : template.unpublished_changes ? (
          <p className="msg info">
            “New content item” still uses the version you last published. Publish to use these changes, or discard them to go back to the published version.
          </p>
        ) : null}
        <div className="cs-editor">
          <div className="cs-left">
            <ArtboardPanel api={editorApi} />
          </div>
          {center}
          <div className="cs-right">
            <Inspector api={editorApi} />
          </div>
        </div>
        {previewOpen && env && <PostPreview layout={shown} env={env} title={template.name} onClose={() => setPreviewOpen(false)} />}
      </div>
    );
  }

  if (!item) return <Loading what="Loading the editor" />;
  return (
    <div className="page cs-editor-page">
      <p className="crumbs small">
        <Link to="/content">Content Studio</Link>
        {item.project && (
          <>
            {' · '}
            <Link to={`/content/projects/${item.project.id}`}>{item.project.title}</Link>
          </>
        )}
      </p>
      <header className="cs-editor-head">
        <div>
          <h1 className="cs-title">{item.title}</h1>
          <p className="muted small">
            {FORMAT_LABEL[item.format]} · <span className="num">{item.width}×{item.height}</span> px · revision <span className="num" data-testid="revision">{base}</span> ·{' '}
            <ContentStatusBadge status={item.status} /> {saveBadge}
          </p>
        </div>
        <div className="row gap wrap">
          {undoRedo}
          <button type="button" className="btn small" onClick={() => setTemplateDialog(true)} disabled={dirty}>
            Save as template
          </button>
        </div>
      </header>
      {saveError}
      {save.msg && save.state !== 'error' && <p className="msg warn">{save.msg}</p>}
      {viewing && (
        <p className="msg info">
          Viewing revision {viewing.number} (read-only).{' '}
          <button type="button" className="btn small" onClick={() => setViewing(null)}>
            Back to the latest
          </button>
        </p>
      )}
      {!viewing && LOCKED_STATUSES.includes(item.status) && !unlocked && (
        <p className="msg info">
          This item is {item.status}: it is shown read-only. Any edit makes a new revision and revokes the approval.{' '}
          <button type="button" className="btn small" onClick={() => setUnlocked(true)}>
            Edit anyway
          </button>
        </p>
      )}
      <div className="cs-editor">
        <div className="cs-left">
          <ArtboardPanel api={editorApi} />
        </div>
        {center}
        <div className="cs-right">
          <WorkflowPanel api={editorApi} item={item} dirty={dirty} isOwner={user?.role === 'owner'} reload={load} env={env} fontsReady={fontsReady} />
          <Inspector api={editorApi} />
          <CopyPanel api={editorApi} />
          <RevisionsPanel
            api={editorApi}
            item={item}
            viewing={viewing}
            onView={async (n) => setViewing(n === base ? null : await api<Revision>(`/api/content-items/${item.id}/revisions/${n}`))}
            onRestore={async (n) => {
              const r = await api<Revision>(`/api/content-items/${item.id}/revisions/${n}`);
              setViewing(null);
              setUnlocked(true);
              commit((l) => Object.assign(l, clone(r.layout!)));
            }}
          />
        </div>
      </div>
      {previewOpen && env && <PostPreview layout={shown} env={env} title={item.title} onClose={() => setPreviewOpen(false)} />}
      {templateDialog && <SaveTemplateDialog itemId={item.id} onClose={() => setTemplateDialog(false)} />}
    </div>
  );
}

export function TemplateStatus({ t }: { t: { is_builtin: boolean; status: string } }) {
  if (t.is_builtin) {
    return (
      <span className="badge" data-status="builtin">
        Built-in
      </span>
    );
  }
  return t.status === 'ready' ? (
    <span className="badge tone-ok" data-status="ready">
      Ready
    </span>
  ) : (
    <span className="badge" data-status="draft">
      Draft
    </span>
  );
}

function WarningsList({ warnings, onPick, template }: { warnings: Warning[]; onPick: (w: Warning) => void; template?: boolean }) {
  if (!warnings.length) return <p className="small ok-text cs-warnings-none">No warnings: images sharp enough, text fits, fonts and logo available.</p>;
  return (
    <ul className="plain cs-warnings" aria-label="Warnings">
      {warnings.map((w, i) => (
        <li key={i} className={w.blocking ? 'blocking' : ''}>
          <button type="button" className="link small" onClick={() => onPick(w)}>
            {w.blocking ? (template ? 'Problem' : 'Blocks approval') : 'Warning'} · artboard {w.artboard + 1}: {w.message}
          </button>
        </li>
      ))}
    </ul>
  );
}

let dragSeq = 0;

/** Where a handle sits on the stage (screen px), kept fully inside it; null when its corner or edge
 *  midpoint lies outside the artboard (a layer wider than the artboard keeps only its visible handles). */
function handleAt(layer: Layer, h: Handle, scale: number, stageW: number, stageH: number): { x: number; y: number } | null {
  const fx = h.includes('w') ? 0 : h.includes('e') ? 1 : 0.5;
  const fy = h.includes('n') ? 0 : h.includes('s') ? 1 : 0.5;
  const x = (layer.x + fx * layer.w) * scale;
  const y = (layer.y + fy * layer.h) * scale;
  if (x < -0.5 || x > stageW + 0.5 || y < -0.5 || y > stageH + 0.5) return null;
  const pad = 6;
  return { x: Math.min(Math.max(x, pad), stageW - pad), y: Math.min(Math.max(y, pad), stageH - pad) };
}

/** Handles that fit a box of this size on screen: a tiny box keeps its middle free so it can still be moved. */
function handlesFor(w: number, h: number): Handle[] {
  const m = Math.min(w, h);
  if (m < 14) return [];
  return m < 44 ? ['nw', 'ne', 'se', 'sw'] : HANDLES;
}

type DragState =
  | { kind: 'move'; id: string; x0: number; y0: number; start: Layer; moved: boolean; key: string; targets: Targets }
  | { kind: 'resize'; handle: Handle; id: string; x0: number; y0: number; start: Layer; moved: boolean; key: string; targets: Targets }
  | { kind: 'pan'; id: string; x0: number; y0: number; start: ImageLayer; natural: { w: number; h: number }; moved: boolean; key: string };

/** The boxes on the artboard: move, resize by 8 handles (Shift on a corner keeps the ratio), snapping
 *  with guide lines (Alt turns it off), Ctrl/Cmd-drag on an image pans the picture in its frame.
 *  Guides and ghosts are DOM, never drawn in the canvas, so they cannot leak into an export. */
function Overlay({ api: e, scale, env }: { api: EditorApi; scale: number; env: RenderEnv | null }) {
  const board = e.layout.artboards[e.sel.ab];
  const drag = useRef<DragState | null>(null);
  const [guides, setGuides] = useState<Guide[]>([]);
  const margin = (e.layout.safe_margin_pct / 100) * e.layout.width * scale;
  const W = e.layout.width;
  const H = e.layout.height;

  function down(ev: React.PointerEvent, layer: Layer, handle: Handle | null) {
    ev.stopPropagation();
    e.setSel({ ab: e.sel.ab, layer: layer.id });
    if (e.readOnly) return;
    (ev.currentTarget as HTMLElement).setPointerCapture(ev.pointerId);
    const base = { id: layer.id, x0: ev.clientX, y0: ev.clientY, moved: false, key: `drag:${layer.id}:${dragSeq++}` };
    const targets = snapTargets(e.layout, e.sel.ab, layer.id);
    if (handle) drag.current = { kind: 'resize', handle, start: structuredClone(layer), targets, ...base };
    else if ((ev.ctrlKey || ev.metaKey) && layer.type === 'image' && layer.fit === 'fill' && e.imageSize(layer.asset_id)) {
      drag.current = { kind: 'pan', start: structuredClone(layer), natural: e.imageSize(layer.asset_id)!, ...base };
    } else drag.current = { kind: 'move', start: structuredClone(layer), targets, ...base };
  }
  function move(ev: React.PointerEvent) {
    const d = drag.current;
    if (!d) return;
    const dx = (ev.clientX - d.x0) / scale;
    const dy = (ev.clientY - d.y0) / scale;
    if (!d.moved && Math.abs(dx) + Math.abs(dy) < 1) return;
    d.moved = true;
    const snapping = !ev.altKey;
    const tol = SNAP_PX / scale;
    let shown: Guide[] = [];
    e.commit((l) => {
      const layer = l.artboards[e.sel.ab].layers.find((x) => x.id === d.id);
      if (!layer) return;
      if (d.kind === 'pan') {
        Object.assign(layer, panFocal(d.start, d.natural, dx, dy));
      } else if (d.kind === 'move') {
        const raw = { x: d.start.x + dx, y: d.start.y + dy, w: d.start.w, h: d.start.h };
        if (snapping) {
          const s = snapMove(raw, d.targets, tol);
          layer.x = s.snappedX ? s.x : Math.round(raw.x);
          layer.y = s.snappedY ? s.y : Math.round(raw.y);
          shown = s.guides;
        } else {
          layer.x = Math.round(raw.x);
          layer.y = Math.round(raw.y);
        }
      } else {
        const r = resizeRect(d.start, d.handle, dx, dy, { keepRatio: ev.shiftKey, targets: snapping ? d.targets : null, tol });
        const snappedGuide = (axis: 'x' | 'y') => r.guides.some((g) => g.axis === axis);
        const round = (v: number, snapped: boolean) => (snapped ? v : Math.round(v));
        layer.x = round(r.rect.x, snappedGuide('x'));
        layer.y = round(r.rect.y, snappedGuide('y'));
        layer.w = round(r.rect.w, snappedGuide('x'));
        layer.h = round(r.rect.h, snappedGuide('y'));
        if (ev.shiftKey && d.handle.length === 2) {
          // the height follows the (rounded) width, so the ratio stays exact to 0.01 px
          layer.h = Math.round((layer.w / (d.start.w / d.start.h)) * 100) / 100;
          layer.y = d.handle.includes('n') ? d.start.y + d.start.h - layer.h : d.start.y;
        }
        shown = r.guides;
      }
    }, d.key);
    setGuides(shown);
  }
  function up() {
    drag.current = null;
    setGuides([]);
  }
  const ghosts = ghostsFor(e.layout, e.sel.ab);
  return (
    <div className="cs-overlay" onPointerDown={() => e.setSel({ ab: e.sel.ab, layer: null })} onPointerMove={move} onPointerUp={up} onPointerCancel={up}>
      <div className="cs-safe" style={{ inset: margin }} aria-hidden="true" />
      {board.layers.map((layer) => {
        if (layer.hidden) return null;
        // a template's empty image box is a placeholder drawn in the canvas, not a missing picture
        const empty = (layer.type === 'image' && e.mode !== 'template' && (!layer.asset_id || !env?.images.has(layer.asset_id))) || (layer.type === 'logo' && !logoFor(env, layer));
        const selected = e.sel.layer === layer.id;
        return (
          <div
            key={layer.id}
            className={`cs-box${selected ? ' selected' : ''}${empty ? ' empty' : ''}${layer.span ? ' span' : ''}`}
            style={{ left: layer.x * scale, top: layer.y * scale, width: layer.w * scale, height: Math.max(layer.h * scale, 2) }}
            onPointerDown={(ev) => down(ev, layer, null)}
            onDoubleClick={() => {
              if (layer.type !== 'text' || e.readOnly) return;
              const field = document.querySelector<HTMLTextAreaElement>('textarea[aria-label="Text content"]');
              field?.focus();
              field?.select();
            }}
            data-layer={layer.id}
            title={layer.name || layer.type}
          >
            {empty && <span className="cs-empty-label">{layer.type === 'logo' ? 'no logo in brand kit' : 'choose an image'}</span>}
            {selected &&
              !e.readOnly &&
              handlesFor(layer.w * scale, layer.h * scale).flatMap((h) => {
                const at = handleAt(layer, h, scale, W * scale, H * scale);
                if (!at) return []; // a handle off the artboard would sit on the panels beside it
                return [
                  <span
                    key={h}
                    className={`cs-handle h-${h}${layer.w * scale < 44 || layer.h * scale < 44 ? ' small' : ''}`}
                    style={{ left: at.x - layer.x * scale, top: at.y - layer.y * scale }}
                    data-handle={h}
                    onPointerDown={(ev) => down(ev, layer, h)}
                    aria-label={`Resize (${h})`}
                  />,
                ];
              })}
          </div>
        );
      })}
      {ghosts.map(({ layer, owner, x }) => {
        const left = Math.max(0, x);
        const right = Math.min(W, x + layer.w);
        const top = Math.max(0, layer.y);
        const bottom = Math.min(H, layer.y + layer.h);
        if (bottom <= top) return null;
        return (
          <div key={`${owner}:${layer.id}`} className="cs-ghost" style={{ left: left * scale, top: top * scale, width: (right - left) * scale, height: (bottom - top) * scale }} data-ghost-of={layer.id}>
            <button
              type="button"
              className="cs-ghost-tag"
              onPointerDown={(ev) => ev.stopPropagation()}
              onClick={() => e.setSel({ ab: owner, layer: layer.id })}
              aria-label={`Continued from slide ${owner + 1}: ${layer.name || layer.type}. Go to that slide.`}
              title={`Continued from slide ${owner + 1}: click to go there`}
            >
              from slide {owner + 1}
            </button>
          </div>
        );
      })}
      {guides.map((g, i) => (
        <div key={i} className={`cs-guide ${g.axis}`} style={g.axis === 'x' ? { left: g.pos * scale } : { top: g.pos * scale }} aria-hidden="true" />
      ))}
    </div>
  );
}

function SaveTemplateDialog({ itemId, onClose }: { itemId: string; onClose: () => void }) {
  const [name, setName] = useState('');
  const [msg, setMsg] = useState('');
  const [error, setError] = useState('');
  async function submit(ev: React.FormEvent) {
    ev.preventDefault();
    try {
      await api('/api/content/templates', { method: 'POST', body: { name, item_id: itemId } });
      setMsg(`Saved “${name}”. It is listed under Brand & templates and in “New content item”.`);
    } catch (e) {
      setError(errorText(e));
    }
  }
  return (
    <Modal
      title="Save as template"
      onClose={onClose}
      footer={
        msg ? (
          <button type="button" className="btn" onClick={onClose}>
            Close
          </button>
        ) : (
          <button type="submit" form="save-template" className="btn primary" disabled={!name.trim()}>
            Save template
          </button>
        )
      }
    >
      <form id="save-template" className="stack" onSubmit={submit}>
        <p className="small muted">Positions are stored relative to the artboard, so the template works at any size. Images and the project's texts are left out of it.</p>
        <label className="field">
          <span>Template name</span>
          <input value={name} onChange={(e) => setName(e.target.value)} autoFocus />
        </label>
        {msg && <p className="msg ok">{msg}</p>}
        <ErrorMsg>{error}</ErrorMsg>
      </form>
    </Modal>
  );
}
